import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Twitter, Linkedin, Youtube, Globe, MapPin, Mail, Phone } from 'lucide-react';
import { resolveLink, useMenu, useSiteSettings } from '@/lib/siteContent';

// Shown until the admin configures web_footer_explore / web_footer_legal menus.
const FALLBACK_EXPLORE = [
  { label: 'About Us', link: 'url:https://localkonnect.com/about.html' },
  { label: 'Events', link: 'events' },
  { label: 'Offers', link: '/offers' },
  { label: 'My Bookings', link: '/my-bookings' },
];
const FALLBACK_LEGAL = [
  { label: 'Terms & Conditions', link: 'url:https://localkonnect.com/terms&conditions.html' },
  { label: 'Privacy Policy', link: 'url:https://localkonnect.com/privacypolicy.html' },
  { label: 'Refund Policy', link: 'url:https://localkonnect.com/refundpolicy.html' },
  { label: 'Cancellation Policy', link: 'url:https://localkonnect.com/cancellation.html' },
];

const SOCIAL_ICONS = { facebook: Facebook, instagram: Instagram, linkedin: Linkedin, twitter: Twitter, x: Twitter, youtube: Youtube };

function FooterLink({ item }) {
  const target = resolveLink(item.link);
  if (!target) return null;
  // 44px rows on phones (a 28px link list is a mis-tap magnet); compact again
  // on desktop where a pointer is precise.
  const cls = 'lk-focus inline-flex min-h-[44px] items-center rounded transition-colors hover:text-white focus-visible:ring-offset-ink-900 md:min-h-0 md:py-1';
  return (
    <li>
      {target.to ? (
        <Link to={target.to} className={cls}>{item.label}</Link>
      ) : (
        <a href={target.href} target="_blank" rel="noopener noreferrer" className={cls}>{item.label}</a>
      )}
    </li>
  );
}

const Footer = () => {
  const settings = useSiteSettings();
  const explore = useMenu('web_footer_explore', FALLBACK_EXPLORE);
  const legal = useMenu('web_footer_legal', FALLBACK_LEGAL);
  const phone = settings.support_phone;
  const phoneDisplay = phone.replace(/^\+91/, '+91 ');

  return (
    <footer className="footer bg-ink-900 text-white pt-12 pb-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 lg:grid-cols-4 lg:gap-8">
          {/* Column 1: Local Konnect */}
          <div className="footer-column col-span-2 space-y-4 lg:col-span-1">
            <h3 className="text-xl font-bold text-white">Local Konnect</h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              Discover and book the best events, gigs and nights out happening near you.
            </p>
            <div className="social-links flex space-x-4">
              {settings.social_links.map((s) => {
                const Icon = SOCIAL_ICONS[s.label.toLowerCase()] ?? Globe;
                return (
                  <a
                    key={s.url}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="lk-focus flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-brand-secondary focus-visible:ring-offset-ink-900"
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Column 2: Explore */}
          <div className="footer-column space-y-4">
            <h3 className="footer-heading">Explore</h3>
            <ul className="space-y-1 text-sm text-gray-400">
              {explore.map((item) => <FooterLink key={item.label} item={item} />)}
            </ul>
          </div>

          {/* Column 3: Information */}
          <div className="footer-column space-y-4">
            <h3 className="footer-heading">Information</h3>
            <ul className="space-y-1 text-sm text-gray-400">
              {legal.map((item) => <FooterLink key={item.label} item={item} />)}
            </ul>
          </div>

          {/* Column 4: Contact Us */}
          <div className="footer-column col-span-2 space-y-4 lg:col-span-1">
            <h3 className="footer-heading">Contact us</h3>
            <ul className="space-y-1 text-sm text-gray-400">
              <li className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-brand-secondary flex-shrink-0" />
                <a href={`tel:${phone.replace(/\s/g, '')}`} className="lk-focus inline-flex min-h-[44px] items-center rounded transition-colors md:min-h-0 md:py-1.5 hover:text-white focus-visible:ring-offset-ink-900">{phoneDisplay}</a>
              </li>
              <li className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-brand-secondary flex-shrink-0" />
                <a href={`mailto:${settings.support_email}`} className="lk-focus inline-flex min-h-[44px] items-center rounded transition-colors md:min-h-0 md:py-1.5 hover:text-white focus-visible:ring-offset-ink-900">{settings.support_email}</a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-brand-secondary flex-shrink-0 mt-0.5" />
                <a
                  href={`https://www.google.com/maps/search/${encodeURIComponent(settings.office_address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="lk-focus inline-flex min-h-[44px] items-center whitespace-pre-line rounded transition-colors md:min-h-0 md:py-1.5 hover:text-white focus-visible:ring-offset-ink-900"
                >
                  {settings.office_address}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Copyright */}
        <div className="copyright border-t border-gray-800 mt-12 pt-8 text-center text-sm text-gray-400">
          <p>&copy; {new Date().getFullYear()} LocalKonnect. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
